/**
 * Template size limits and the one validator every save goes through.
 *
 * Mirrored in spt/src/helpers/template-limits.ts, fixture for fixture. The
 * editor disables the add action at a limit; the backend refuses an over-limit
 * payload with the same reason, so a stale or hand-rolled client can never
 * store more than the apply paths were sized for (about 150 mutations for a
 * subitem template, about 900 for a group template). Change both files together.
 *
 * Group template `content`:
 *   { group: { title, color },
 *     item_columns:    [{ id, title, type }],
 *     subitem_columns: [{ id, title, type }],
 *     items: [{ key, name, column_values: [], updates: [],
 *               subitems: [{ key, name, column_values: [], updates: [] }] }] }
 * An update: { body, mentions: [{ id, type, name }] }.
 */
export const SUBITEM_LIMITS = Object.freeze({
  subitems: 50,
  updatesPerRow: 5,
  updatesTotal: 100,
  updateBodyChars: 5000,
  mentionsPerUpdate: 10,
  nameChars: 255,
});

export const GROUP_LIMITS = Object.freeze({
  items: 100,
  subitemsPerItem: 20,
  subitemsTotal: 500,
  updatesPerRow: 5,
  updatesTotal: 300,
  updateBodyChars: 5000,
  mentionsPerUpdate: 10,
  nameChars: 255,
});

/** A stored template without a `type` is a subitem template (rows older than this column). */
export function templateType(template) {
  return template && template.type === 'group' ? 'group' : 'subitem';
}

function nameProblem(name, what, max, required) {
  if (name === undefined || name === null) return required ? `${what} needs a name` : null;
  if (typeof name !== 'string') return `${what} name must be text`;
  if (required && name.trim() === '') return `${what} needs a name`;
  if (name.length > max) return `${what} name is longer than ${max} characters`;
  return null;
}

/** Validates one row's `updates`; returns the count so callers can total them. */
function updatesProblem(updates, what, limits) {
  if (updates === undefined || updates === null) return { count: 0, reason: null };
  if (!Array.isArray(updates)) return { count: 0, reason: `${what} updates must be a list` };
  if (updates.length > limits.updatesPerRow) {
    return { count: updates.length, reason: `${what} has ${updates.length} updates; the limit is ${limits.updatesPerRow} per row` };
  }
  for (const u of updates) {
    if (!u || typeof u !== 'object' || typeof u.body !== 'string') return { count: updates.length, reason: `${what} has an update without a text body` };
    if (u.body.length > limits.updateBodyChars) {
      return { count: updates.length, reason: `${what} has an update longer than ${limits.updateBodyChars} characters` };
    }
    if (u.mentions !== undefined && u.mentions !== null) {
      if (!Array.isArray(u.mentions)) return { count: updates.length, reason: `${what} has an update whose mentions are not a list` };
      if (u.mentions.length > limits.mentionsPerUpdate) {
        return { count: updates.length, reason: `${what} has an update with ${u.mentions.length} mentions; the limit is ${limits.mentionsPerUpdate}` };
      }
    }
  }
  return { count: updates.length, reason: null };
}

function validateSubitemTemplate(payload) {
  const L = SUBITEM_LIMITS;
  const subitems = payload.subitems == null ? [] : payload.subitems;
  if (!Array.isArray(subitems)) return { ok: false, reason: 'subitems must be a list' };
  if (subitems.length > L.subitems) {
    return { ok: false, reason: `A template can hold up to ${L.subitems} subitems (got ${subitems.length})` };
  }
  let updatesTotal = 0;
  for (let i = 0; i < subitems.length; i++) {
    const row = subitems[i];
    const what = `Subitem ${i + 1}`;
    if (!row || typeof row !== 'object') return { ok: false, reason: `${what} is not a row` };
    const n = nameProblem(row.name, what, L.nameChars, false);
    if (n) return { ok: false, reason: n };
    const u = updatesProblem(row.updates, what, L);
    if (u.reason) return { ok: false, reason: u.reason };
    updatesTotal += u.count;
  }
  if (updatesTotal > L.updatesTotal) {
    return { ok: false, reason: `A template can hold up to ${L.updatesTotal} updates in total (got ${updatesTotal})` };
  }
  return { ok: true };
}

function validateGroupTemplate(payload) {
  const L = GROUP_LIMITS;
  const content = payload.content;
  if (!content || typeof content !== 'object') return { ok: false, reason: 'A group template needs content' };
  const g = nameProblem(content.group && content.group.title, 'The group', L.nameChars, true);
  if (g) return { ok: false, reason: g };
  for (const key of ['item_columns', 'subitem_columns']) {
    if (content[key] !== undefined && !Array.isArray(content[key])) return { ok: false, reason: `${key} must be a list` };
  }
  const items = content.items == null ? [] : content.items;
  if (!Array.isArray(items)) return { ok: false, reason: 'items must be a list' };
  if (items.length > L.items) {
    return { ok: false, reason: `A group template can hold up to ${L.items} items (got ${items.length})` };
  }
  let subitemsTotal = 0;
  let updatesTotal = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const what = `Item ${i + 1}`;
    if (!item || typeof item !== 'object') return { ok: false, reason: `${what} is not a row` };
    const n = nameProblem(item.name, what, L.nameChars, false);
    if (n) return { ok: false, reason: n };
    const u = updatesProblem(item.updates, what, L);
    if (u.reason) return { ok: false, reason: u.reason };
    updatesTotal += u.count;

    const subitems = item.subitems == null ? [] : item.subitems;
    if (!Array.isArray(subitems)) return { ok: false, reason: `${what} subitems must be a list` };
    if (subitems.length > L.subitemsPerItem) {
      return { ok: false, reason: `${what} has ${subitems.length} subitems; the limit is ${L.subitemsPerItem} per item` };
    }
    subitemsTotal += subitems.length;
    for (let j = 0; j < subitems.length; j++) {
      const sub = subitems[j];
      const sw = `${what}, subitem ${j + 1}`;
      if (!sub || typeof sub !== 'object') return { ok: false, reason: `${sw} is not a row` };
      const sn = nameProblem(sub.name, sw, L.nameChars, false);
      if (sn) return { ok: false, reason: sn };
      const su = updatesProblem(sub.updates, sw, L);
      if (su.reason) return { ok: false, reason: su.reason };
      updatesTotal += su.count;
    }
  }
  if (subitemsTotal > L.subitemsTotal) {
    return { ok: false, reason: `A group template can hold up to ${L.subitemsTotal} subitems in total (got ${subitemsTotal})` };
  }
  if (updatesTotal > L.updatesTotal) {
    return { ok: false, reason: `A group template can hold up to ${L.updatesTotal} updates in total (got ${updatesTotal})` };
  }
  return { ok: true };
}

/**
 * The whole save payload: `{ name, type?, access_level?, columns?, subitems?, content? }`.
 * Returns `{ ok: true }` or `{ ok: false, reason }` with the first problem found, worded for the user.
 */
export function validateTemplatePayload(payload) {
  if (!payload || typeof payload !== 'object') return { ok: false, reason: 'Template payload is required' };
  const n = nameProblem(payload.name, 'The template', SUBITEM_LIMITS.nameChars, true);
  if (n) return { ok: false, reason: n };
  if (payload.type !== undefined && payload.type !== null && payload.type !== 'subitem' && payload.type !== 'group') {
    return { ok: false, reason: `Unknown template type "${String(payload.type)}"` };
  }
  return templateType(payload) === 'group' ? validateGroupTemplate(payload) : validateSubitemTemplate(payload);
}
